import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import useAlerts from '../useAlerts';
import useLoading from '../useLoading';
import useModals from '../useModals';
import useConfirmModal from '../useConfirmModal';
import {
  clearProductToEdit,
  closeAddProductModal,
  closeEditProductModal,
  openAddProductModal,
  openEditProductModal,
} from '../../store/slices/purchases/creditNoteSlice';
import { useBasePurchase } from './useBasePurchase';
import { updateCreditNoteTotals } from '../../store/slices/purchases/basePurchaseSlice';
import CreditNoteService from '../../services/purchases/creditNoteService';

export const useCreditNote = () => {
  const dispatch = useDispatch();
  const { show: showAlert } = useAlerts();
  const { openModal } = useModals();
  const confirm = useConfirmModal();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { addProduct, deleteProduct } = useBasePurchase();

  const { productToEdit, showAddProductModal, showEditProductModal } = useSelector(
    (state: RootState) => state.creditNote
  );

  const handlerAddProduct = async (product: any) => {
    try {
      showLoadingIndicator();
  
      const response = await CreditNoteService.addProduct(product);
      console.log(response);
      if (response.statusCode === 201) {
        const { title, message, type, data } = response;
        const { products, totals } = data;
        addProduct(products);
  
        dispatch(updateCreditNoteTotals(totals));
  
        dispatch(closeAddProductModal());
        openModal(type, { title, message });
      } else {
        showAlert('error', 'Error', 'No se pudo agregar el producto.');
      }
    } catch (error: any) {
      const response = error?.response?.data;
      if (response?.statusCode === 404 || response?.statusCode === 400) {
        openModal(response.type || 'error', {
          title: response.title || 'Error',
          message: response.message || 'Ocurrió un error al agregar el producto.',
        });
      } else {
        showAlert('error', 'Error', 'Ocurrió un error inesperado.');
      }
      console.error('❌ Error al agregar producto:', error);
    } finally {
      hideLoadingIndicator();
    }
  };
  



  const handlerDeleteProduct = async (id: number) => {
    confirm({
      title: '¿Eliminar producto?',
      message: '¿Estás seguro que deseas eliminar este producto?',
      icon: 'warning',
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      confirmArgs: id,
      onConfirm: async (id) => {
        try {
          showLoadingIndicator();
  
          const response = await CreditNoteService.deleteProduct(id);
          if (response.statusCode === 200) {
            const { title, message, type, data } = response;
            const { totals } = data;
  
            deleteProduct(id); // ✅ Elimina del store local
            dispatch(updateCreditNoteTotals(totals)); // ✅ Totales de la nota de crédito
  
            openModal(type, { title, message });
          } else {
            showAlert('error', 'Error', 'No se pudo eliminar el producto.');
          }
        } catch (error) {
          console.error('❌ Error al eliminar producto:', error);
          showAlert('error', 'Error', 'Ocurrió un error inesperado.');
        } finally {
          hideLoadingIndicator();
        }
      },
    });
  };
  


  return {
    productToEdit,
    showAddProductModal,
    showEditProductModal,
    handlerAddProduct,
    handlerDeleteProduct,
    clearProductToEdit: () => dispatch(clearProductToEdit()),
    openAddProductModal: () => dispatch(openAddProductModal()),
    closeAddProductModal: () => dispatch(closeAddProductModal()),
    openEditProductModal: () => dispatch(openEditProductModal()),
    closeEditProductModal: () => dispatch(closeEditProductModal()),
  };
};
